import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/context/AdminAuthContext";
import { imgSrc } from "@/lib/img";
import { Card, PageTitle, Btn, Empty, Field, Input, Textarea, Select, Toggle } from "@/admin/ui";

const EMPTY = { name: "", location: "", quote: "", photo: null, rating: 5, order: 99, active: true };

const TestimonialForm = ({ item, onSaved, onCancel }) => {
  const [form, setForm] = useState({ ...EMPTY, ...item });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post("/admin/upload", fd);
      setForm((f) => ({ ...f, photo: data.url }));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, rating: Number(form.rating) || 5, order: Number(form.order) || 0 };
      item?.id ? await adminApi.put(`/admin/testimonials/${item.id}`, payload) : await adminApi.post("/admin/testimonials", payload);
      toast.success("Testimonial saved");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <form data-testid="testimonial-form" onSubmit={save} className="space-y-4">
      <Field label="Quote"><Textarea data-testid="testimonial-quote-input" rows={4} value={form.quote} onChange={on("quote")} required /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Customer name"><Input data-testid="testimonial-name-input" value={form.name} onChange={on("name")} required /></Field>
        <Field label="Location"><Input data-testid="testimonial-location-input" value={form.location} onChange={on("location")} placeholder="Bengaluru" /></Field>
      </div>
      <div className="grid sm:grid-cols-[120px_120px_1fr] gap-4 items-end">
        <Field label="Rating"><Select data-testid="testimonial-rating-input" value={form.rating} onChange={on("rating")}>{[5,4,3,2,1].map((n)=><option key={n} value={n}>{n} stars</option>)}</Select></Field>
        <Field label="Order"><Input data-testid="testimonial-order-input" type="number" value={form.order} onChange={on("order")} /></Field>
        <Toggle testId="testimonial-active-toggle" checked={form.active} onChange={(v) => setForm((f) => ({ ...f, active: v }))} label={form.active ? "Shown on site" : "Hidden"} />
      </div>
      <Field label="Photo (optional)">
        <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" />
        {form.photo ? (
          <div className="relative inline-block">
            <img src={imgSrc(form.photo)} alt="" className="w-20 h-20 rounded-full object-cover border border-forest/15" />
            <button type="button" onClick={() => setForm((f) => ({ ...f, photo: null }))} className="absolute -top-1 -right-1 bg-forest text-cream rounded-full p-1"><X size={11} /></button>
          </div>
        ) : (
          <button type="button" data-testid="testimonial-photo-button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 text-xs font-semibold text-forest border border-dashed border-forest/30 rounded-xl px-4 py-3 hover:border-gold hover:text-gold">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} Upload photo
          </button>
        )}
      </Field>
      <div className="flex justify-end gap-2">
        <Btn type="button" variant="ghost" onClick={onCancel}>Cancel</Btn>
        <Btn type="submit" busy={busy} data-testid="testimonial-form-save">Save</Btn>
      </div>
    </form>
  );
};

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => adminApi.get("/admin/testimonials").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (t) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try { await adminApi.delete(`/admin/testimonials/${t.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (editing !== null) {
    return (
      <div>
        <PageTitle title={editing?.id ? "Edit testimonial" : "New testimonial"} />
        <Card className="p-6 sm:p-8"><TestimonialForm item={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} /></Card>
      </div>
    );
  }

  return (
    <div data-testid="admin-testimonials-page">
      <PageTitle title="Testimonials" sub="Curated quotes shown on the Home page." action={<Btn data-testid="add-testimonial-button" onClick={() => setEditing({})}><Plus size={14} /> Add testimonial</Btn>} />
      <Card className="overflow-hidden">
        {items.length === 0 && <Empty text="No testimonials yet." />}
        <ul className="divide-y divide-forest/10">
          {items.map((t) => (
            <li key={t.id} data-testid={`admin-testimonial-row-${t.id}`} className={`flex items-start gap-4 px-4 sm:px-6 py-4 ${t.active === false ? "opacity-50" : ""}`}>
              {t.photo ? <img src={imgSrc(t.photo)} alt="" className="w-12 h-12 rounded-full object-cover shrink-0" /> : <span className="w-12 h-12 rounded-full bg-forest/10 text-forest font-serif flex items-center justify-center shrink-0">{t.name.charAt(0)}</span>}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-mutedgreen line-clamp-2">"{t.quote}"</p>
                <p className="text-[11px] text-forest font-semibold mt-1">{t.name}{t.location ? ` · ${t.location}` : ""} · {t.rating}★</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button data-testid={`edit-testimonial-${t.id}`} onClick={() => setEditing(t)} aria-label="Edit" className="p-2 rounded-full text-forest hover:bg-forest/5"><Pencil size={15} /></button>
                <button data-testid={`delete-testimonial-${t.id}`} onClick={() => remove(t)} aria-label="Delete" className="p-2 rounded-full text-mutedgreen hover:text-red-700 hover:bg-red-50"><Trash2 size={15} /></button>
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
